import { useAtomValue } from "jotai";
import {
    balanceSheetMetricsAtom,
    balanceSheetResolutionAtom,
} from "../../metrics.state";
import {
    FinanceLabField,
    FinanceLabFieldType,
    formatFinanceLabValue,
} from "@/entities/FinanceLab";
import { AccountLabel } from "@/entities/Labeling/Account.model";
import { Metric } from "@/entities/Finance/Finance.model";
import { useFlags } from "@/utils/flagsmith/useFlags";
import dayjs from "dayjs";
import { applyWindowToMetrics } from "../../utils/applyWindowToMetric";
import { getMetric } from "../../utils/getMetric";

export interface SummaryTableRow {
    metric: string;
    metricLabel?: string;
    periodChange?: number | null;
    [key: string]: string | number | (() => void) | undefined | null;
    onClick?: () => void;
}

interface PeriodColumn {
    field: string;
    headerName: string;
}

export interface SummaryBalanceSheetResult {
    data: SummaryTableRow[];
    columns: PeriodColumn[];
}

function formatPeriodHeader(period: string): string {
    const date = dayjs(period);
    if (!date.isValid()) return period;

    if (period.length === 4) {
        return period;
    } else if (period.includes("Q")) {
        return period.replace("-", "-").slice(0, -2);
    } else {
        return date.format("MM-YY");
    }
}

function calculatePeriodDelta(
    firstValue: number | null,
    lastValue: number | null,
): number | null {
    if (firstValue === null || lastValue === null || firstValue === 0) {
        return null;
    }
    const delta = (lastValue - firstValue) / Math.abs(firstValue);
    return delta;
}

export function useSummaryBalanceSheetData(): SummaryBalanceSheetResult {
    const flags = useFlags();
    const showComingSoonFields = flags.enabled("show-coming-soon-placeholders");
    const balanceSheetMetrics = useAtomValue(balanceSheetMetricsAtom);
    const metricResolution = useAtomValue(balanceSheetResolutionAtom);

    const windowedMetrics = applyWindowToMetrics(
        balanceSheetMetrics,
        metricResolution.window,
    );

    const totalAssets = getMetric(
        windowedMetrics,
        AccountLabel.ASSETS,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS,
    );
    const cash = getMetric(
        windowedMetrics,
        AccountLabel.CURRENT_ASSETS_CASH_AND_CASH_EQUIVALENTS,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH,
    );
    const accountsReceivable = getMetric(
        windowedMetrics,
        AccountLabel.CURRENT_ASSETS_NET_RECEIVABLES,
    );
    const inventory = getMetric(
        windowedMetrics,
        AccountLabel.CURRENT_ASSETS_NET_INVENTORY,
    );
    const grossPPE = getMetric(
        windowedMetrics,
        AccountLabel.NONCURRENT_ASSETS_PROPERTY_PLANT_AND_EQUIPMENT,
    );
    const totalLiabilities = getMetric(
        windowedMetrics,
        AccountLabel.LIABILITIES,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_LIABILITIES,
    );
    const accountsPayable = getMetric(
        windowedMetrics,
        AccountLabel.CURRENT_LIABILITIES_ACCOUNTS_PAYABLE,
    );
    const currentDebt = getMetric(
        windowedMetrics,
        AccountLabel.CURRENT_LIABILITIES_DEBT,
    );

    const allPeriods = new Set<string>();
    [
        totalAssets,
        cash,
        accountsReceivable,
        inventory,
        grossPPE,
        totalLiabilities,
        accountsPayable,
        currentDebt,
    ].forEach((metric) => {
        metric?.values.forEach((v) => allPeriods.add(v.period));
    });

    const sortedPeriods = Array.from(allPeriods).sort();

    const periodColumns: PeriodColumn[] = sortedPeriods.map((period) => ({
        field: period,
        headerName: formatPeriodHeader(period),
    }));

    const changeLabel =
        sortedPeriods.length >= 2
            ? `% Δ ${formatPeriodHeader(sortedPeriods[sortedPeriods.length - 2])}—${formatPeriodHeader(sortedPeriods[sortedPeriods.length - 1])}`
            : "% Δ";

    const columns: PeriodColumn[] = [
        { field: "metric", headerName: "" },
        ...periodColumns,
        { field: "periodChange", headerName: changeLabel },
    ];

    function createRow(
        label: string,
        metric: Metric | undefined,
        fieldType: FinanceLabFieldType,
        metricLabel?: string,
    ): SummaryTableRow {
        const row: SummaryTableRow = {
            metric: label,
            metricLabel,
        };

        if (!metric) {
            sortedPeriods.forEach((period) => {
                row[period] = "-";
            });
            row.periodChange = null;
            return row;
        }

        const valuesByPeriod = new Map(
            metric.values.map((v) => [v.period, v.value]),
        );

        sortedPeriods.forEach((period) => {
            const value = valuesByPeriod.get(period);
            row[period] =
                value !== undefined
                    ? formatFinanceLabValue(value, fieldType)
                    : "-";
        });

        const secondLastValue =
            sortedPeriods.length >= 2
                ? valuesByPeriod.get(sortedPeriods[sortedPeriods.length - 2]) ??
                  null
                : null;
        const lastValue =
            valuesByPeriod.get(sortedPeriods[sortedPeriods.length - 1]) ?? null;
        row.periodChange = calculatePeriodDelta(secondLastValue, lastValue);

        return row;
    }

    const data: SummaryTableRow[] = [
        createRow(
            "Total Assets",
            totalAssets,
            FinanceLabFieldType.CURRENCY,
            AccountLabel.ASSETS,
        ),
        createRow(
            "Cash & Equivalents",
            cash,
            FinanceLabFieldType.CURRENCY,
            AccountLabel.CURRENT_ASSETS_CASH_AND_CASH_EQUIVALENTS,
        ),
        ...(showComingSoonFields
            ? [
                  createRow(
                      "Accounts Receivable",
                      accountsReceivable,
                      FinanceLabFieldType.CURRENCY,
                      AccountLabel.CURRENT_ASSETS_NET_RECEIVABLES,
                  ),
                  createRow(
                      "Inventory",
                      inventory,
                      FinanceLabFieldType.CURRENCY,
                      AccountLabel.CURRENT_ASSETS_NET_INVENTORY,
                  ),
                  createRow(
                      "Gross PP&E",
                      grossPPE,
                      FinanceLabFieldType.CURRENCY,
                      AccountLabel.NONCURRENT_ASSETS_PROPERTY_PLANT_AND_EQUIPMENT,
                  ),
              ]
            : accountsReceivable || inventory || grossPPE
              ? [
                    ...(accountsReceivable
                        ? [
                              createRow(
                                  "Accounts Receivable",
                                  accountsReceivable,
                                  FinanceLabFieldType.CURRENCY,
                                  AccountLabel.CURRENT_ASSETS_NET_RECEIVABLES,
                              ),
                          ]
                        : []),
                    ...(inventory
                        ? [
                              createRow(
                                  "Inventory",
                                  inventory,
                                  FinanceLabFieldType.CURRENCY,
                                  AccountLabel.CURRENT_ASSETS_NET_INVENTORY,
                              ),
                          ]
                        : []),
                    ...(grossPPE
                        ? [
                              createRow(
                                  "Gross PP&E",
                                  grossPPE,
                                  FinanceLabFieldType.CURRENCY,
                                  AccountLabel.NONCURRENT_ASSETS_PROPERTY_PLANT_AND_EQUIPMENT,
                              ),
                          ]
                        : []),
                ]
              : []),
        createRow(
            "Total Liabilities",
            totalLiabilities,
            FinanceLabFieldType.CURRENCY,
            AccountLabel.LIABILITIES,
        ),
        ...(showComingSoonFields
            ? [
                  createRow(
                      "Accounts Payable",
                      accountsPayable,
                      FinanceLabFieldType.CURRENCY,
                      AccountLabel.CURRENT_LIABILITIES_ACCOUNTS_PAYABLE,
                  ),
                  createRow(
                      "Current Debt Balance",
                      currentDebt,
                      FinanceLabFieldType.CURRENCY,
                      AccountLabel.CURRENT_LIABILITIES_DEBT,
                  ),
              ]
            : accountsPayable || currentDebt
              ? [
                    ...(accountsPayable
                        ? [
                              createRow(
                                  "Accounts Payable",
                                  accountsPayable,
                                  FinanceLabFieldType.CURRENCY,
                                  AccountLabel.CURRENT_LIABILITIES_ACCOUNTS_PAYABLE,
                              ),
                          ]
                        : []),
                    ...(currentDebt
                        ? [
                              createRow(
                                  "Current Debt Balance",
                                  currentDebt,
                                  FinanceLabFieldType.CURRENCY,
                                  AccountLabel.CURRENT_LIABILITIES_DEBT,
                              ),
                          ]
                        : []),
                ]
              : []),
    ];

    return { data, columns };
}
